import { Box, type SxProps, Tab, Tabs, type Theme, Typography } from '@mui/material';
import { NamedLayoutBlock } from 'src/components';
import { useUrlParamTab } from 'src/utils/hooks';
import { ClientSegmentsTab } from './client-segments/client-segments-tab';
import { CompanyDetailsTab } from './company-details/company-details-tab';

const TAB_SEARCH_PARAM = 'tab';

enum CompanyTab {
  ClientSegments = 'client-segments',
  CompanyDetails = 'company-details',
}

const COMPANY_TAB_VALUES = [CompanyTab.ClientSegments, CompanyTab.CompanyDetails] as const;

const layoutSx: SxProps<Theme> = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
};

const layoutBlockContentSx: SxProps<Theme> = {
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  overflow: 'auto',
};

const blockContentProps = {
  sx: layoutBlockContentSx,
};

const contentColumnSx: SxProps<Theme> = {
  width: '100%',
  maxWidth: '45rem',
  display: 'flex',
  flexDirection: 'column',
  padding: (theme: Theme) => ({
    xs: theme.spacing(3, 2, 5),
    sm: theme.spacing(4, 3, 6),
  }),
  minWidth: 0,
};

const tabsSx: SxProps<Theme> = {
  mt: 2,
  borderBottom: 1,
  borderColor: (theme: Theme) => theme.palette.custom.OV.sys.outlineVariant,
  maxWidth: '100%',
  '& .MuiTabs-indicator': {
    backgroundColor: (theme: Theme) => theme.palette.custom.OV.sys.primary,
  },
  '& .MuiTab-root': {
    minWidth: {
      xs: 'auto',
      sm: 160,
    },
  },
  '& .MuiTab-root.Mui-selected': {
    color: (theme: Theme) => theme.palette.custom.OV.sys.primary,
  },
};

export const CompanyInfoView = () => {
  const { activeTab, setActiveTab } = useUrlParamTab<CompanyTab>({
    paramName: TAB_SEARCH_PARAM,
    validValues: COMPANY_TAB_VALUES,
    defaultValue: CompanyTab.ClientSegments,
  });

  return (
    <NamedLayoutBlock
      title="Settings / Company"
      sx={layoutSx}
      BlockContentProps={blockContentProps}
    >
      <Box sx={contentColumnSx}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Company
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Manage your client segments, support details, and billing for your workspace.
          </Typography>
        </Box>

        <Tabs
          value={activeTab}
          onChange={(_, value: CompanyTab) => setActiveTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={tabsSx}
        >
          <Tab label="Client segments" value={CompanyTab.ClientSegments} />
          <Tab label="Company details" value={CompanyTab.CompanyDetails} />
        </Tabs>
        {activeTab === CompanyTab.ClientSegments ? (
          <ClientSegmentsTab />
        ) : (
          <CompanyDetailsTab />
        )}
      </Box>
    </NamedLayoutBlock>
  );
};
